/**
 * Industry Configuration
 * 
 * Single source of truth for industry presets. Maps industryId to defaults
 * for call types, lead form fields, and AI prompt context.
 * 
 * The AI prompts use the free-text `industryDescription` from the team doc,
 * NOT these presets. These presets provide nice defaults for non-AI UI elements.
 */

// Legacy call type values from before industry support was added.
// Used to display readable labels for old evaluation data.
const LEGACY_CALL_TYPE_MAP = {
  'real-estate-seller': 'Seller Call',
  'real-estate-buyer': 'Buyer Call',
  'real-estate-followup': 'Follow-up Call',
};

export const INDUSTRY_PRESETS = {
  'real-estate-investor': {
    label: 'Real Estate - Investor/Wholesaler',
    callTypes: [
      { value: 'seller-call', label: 'Seller Call' },
      { value: 'buyer-call', label: 'Buyer Call' },
      { value: 'follow-up', label: 'Follow-up Call' },
    ],
    defaultLeadFields: [
      { id: 'prospectName', label: 'Prospect Name', type: 'text', required: true },
      { id: 'prospectPhone', label: 'Phone Number', type: 'tel', required: true },
      { id: 'prospectEmail', label: 'Email Address', type: 'email', required: false },
      { id: 'prospectAddress', label: 'Property Address', type: 'text', required: false },
    ],
    description: 'Real estate investment — cold calling property owners to purchase distressed properties for renovation, resale, or rental portfolio building. Focus on seller motivation, property condition, timeline, and scheduling in-person appointments.',
  },
  'real-estate-agency': {
    label: 'Real Estate - Agency/Brokerage',
    callTypes: [
      { value: 'listing-call', label: 'Listing Call' },
      { value: 'buyer-call', label: 'Buyer Call' },
      { value: 'follow-up', label: 'Follow-up Call' },
    ],
    defaultLeadFields: [
      { id: 'prospectName', label: 'Prospect Name', type: 'text', required: true },
      { id: 'prospectPhone', label: 'Phone Number', type: 'tel', required: true },
      { id: 'prospectEmail', label: 'Email Address', type: 'email', required: false },
      { id: 'prospectAddress', label: 'Property Address', type: 'text', required: false },
    ],
    description: 'Real estate agency — connecting home buyers and sellers, scheduling showings and listings, qualifying buyer/seller readiness, and building long-term client relationships.',
  },
  'insurance': {
    label: 'Insurance',
    callTypes: [
      { value: 'new-prospect', label: 'New Prospect' },
      { value: 'quote-follow-up', label: 'Quote Follow-up' },
      { value: 'renewal', label: 'Policy Renewal' },
    ],
    defaultLeadFields: [
      { id: 'prospectName', label: 'Prospect Name', type: 'text', required: true },
      { id: 'prospectPhone', label: 'Phone Number', type: 'tel', required: true },
      { id: 'prospectEmail', label: 'Email Address', type: 'email', required: false },
      { id: 'currentProvider', label: 'Current Provider', type: 'text', required: false },
    ],
    description: 'Insurance sales — identifying coverage gaps, quoting policies, overcoming price objections, and booking consultations to review coverage needs.',
  },
  'other': {
    label: 'Other (describe your business)',
    callTypes: [
      { value: 'outbound', label: 'Outbound Call' },
      { value: 'inbound', label: 'Inbound Call' },
      { value: 'follow-up', label: 'Follow-up Call' },
    ],
    defaultLeadFields: [
      { id: 'prospectName', label: 'Contact Name', type: 'text', required: true },
      { id: 'prospectPhone', label: 'Phone Number', type: 'tel', required: true },
      { id: 'prospectEmail', label: 'Email Address', type: 'email', required: false },
      { id: 'company', label: 'Company', type: 'text', required: false },
    ],
    description: '', // User types their own
  },
};

// Default fallback industry for existing teams without industryId
const DEFAULT_INDUSTRY_ID = 'real-estate-investor';

// Generic fallback for unknown industry IDs
const GENERIC_INDUSTRY_ID = 'other';

/**
 * Get the call types for a given industry.
 * Falls back to real-estate-investor for undefined/null (backwards compat for existing teams).
 * Falls back to generic for unknown industry IDs.
 */
export function getCallTypes(industryId) {
  if (industryId === undefined || industryId === null) {
    return INDUSTRY_PRESETS[DEFAULT_INDUSTRY_ID].callTypes;
  }
  if (INDUSTRY_PRESETS[industryId]) {
    return INDUSTRY_PRESETS[industryId].callTypes;
  }
  return INDUSTRY_PRESETS[GENERIC_INDUSTRY_ID].callTypes;
}

/**
 * Get the default lead form fields for a given industry.
 * Falls back to real-estate-investor for undefined/null (backwards compat).
 */
export function getDefaultLeadFields(industryId) {
  if (industryId === undefined || industryId === null) {
    return INDUSTRY_PRESETS[DEFAULT_INDUSTRY_ID].defaultLeadFields;
  }
  if (INDUSTRY_PRESETS[industryId]) {
    return INDUSTRY_PRESETS[industryId].defaultLeadFields;
  }
  return INDUSTRY_PRESETS[GENERIC_INDUSTRY_ID].defaultLeadFields;
}

/**
 * Get the auto-populated description for a given industry preset.
 * Returns empty string for 'other' (user types their own).
 */
export function getIndustryDescription(industryId) {
  if (INDUSTRY_PRESETS[industryId]) {
    return INDUSTRY_PRESETS[industryId].description;
  }
  return '';
}

/**
 * Get the label for an industry preset.
 */
export function getIndustryLabel(industryId) {
  if (INDUSTRY_PRESETS[industryId]) {
    return INDUSTRY_PRESETS[industryId].label;
  }
  return 'Unknown Industry';
}

/**
 * Resolve a stored call type value to its display label.
 * Handles legacy values (real-estate-seller, etc.) and current values.
 */
export function getCallTypeLabel(callTypeValue, industryId) {
  // Check legacy values first
  if (LEGACY_CALL_TYPE_MAP[callTypeValue]) {
    return LEGACY_CALL_TYPE_MAP[callTypeValue];
  }

  // Check current industry call types
  const callTypes = getCallTypes(industryId);
  const match = callTypes.find(ct => ct.value === callTypeValue);
  if (match) {
    return match.label;
  }

  // Check all presets in case the value belongs to a different industry
  for (const preset of Object.values(INDUSTRY_PRESETS)) {
    const found = preset.callTypes.find(ct => ct.value === callTypeValue);
    if (found) {
      return found.label;
    }
  }

  // Return raw value as last resort (no crash)
  return callTypeValue || 'Unknown';
}

/**
 * Get the list of all available industry presets for dropdowns.
 */
export function getIndustryOptions() {
  return Object.entries(INDUSTRY_PRESETS).map(([id, preset]) => ({
    value: id,
    label: preset.label,
  }));
}

/**
 * Check if an industryId is a real-estate variant.
 */
export function isRealEstateIndustry(industryId) {
  return industryId === 'real-estate-investor' || industryId === 'real-estate-agency';
}

/**
 * Get default lead capture form config for an industry.
 */
export function getDefaultLeadCaptureForm(industryId) {
  return {
    title: 'Lead Data',
    fields: getDefaultLeadFields(industryId),
  };
}

/**
 * Format a user's display name from their user document fields.
 * Falls back to email prefix, then 'Unknown'.
 */
export function formatUserName(user) {
  if (!user) return 'Unknown';

  const firstName = (typeof user.firstName === 'string' ? user.firstName : '').trim();
  const lastName = (typeof user.lastName === 'string' ? user.lastName : '').trim();

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) {
    return firstName;
  }
  if (lastName) {
    return lastName;
  }
  if (typeof user.email === 'string' && user.email) {
    return user.email.split('@')[0];
  }
  if (typeof user.userEmail === 'string' && user.userEmail) {
    return user.userEmail.split('@')[0];
  }
  return 'Unknown';
}
